import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type PreferencesState = {
  /** Show the timestamp on each announcement in the notifications list. */
  messageTime: boolean;
  /**
   * Alert the user when one of their bets wins.
   *
   * TODO(df): persisted but not yet consumed — this build has no win-alert
   * channel to gate. Wire it at whatever pushes the alert once that ships;
   * until then the toggle remembers the choice and changes nothing.
   */
  prizeNotification: boolean;
  /**
   * Suppress operator announcements — the popup over Home and the queue
   * that plays straight after sign-in.
   */
  quietMode: boolean;
  set: (patch: Partial<Omit<PreferencesState, 'set'>>) => void;
};

/**
 * Device-level preferences, set on the Settings page.
 *
 * Local and persisted rather than server-side: none of these has a backend
 * field, and they describe how this device behaves rather than what the
 * account is. "Clear cache" deliberately preserves the key — a preference
 * is not a cache.
 */
export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      // Defaults match the states the design shows on a fresh install.
      messageTime: true,
      prizeNotification: true,
      quietMode: false,
      set: (patch) => set(patch),
    }),
    { name: 'hg.prefs' },
  ),
);

/**
 * Whether operator announcements may be shown right now.
 *
 * Read outside React too (the sign-in path calls it before any component
 * mounts), so it goes through `getState` rather than the hook.
 */
export function announcementsAllowed(): boolean {
  return !usePreferencesStore.getState().quietMode;
}
